import React from "react";
import { fieldLabel } from "./analysisFieldMeta";
import {
  formatFieldValue,
  showStatusChip,
  statusChipClass,
  statusLabel,
} from "./analysisUtils";
import { formatGapListItem, humanizeWarningToken } from "./analysisDisplayUtils";

const GAP_FIELD_IDS = new Set([
  "design.design_gaps",
  "sti.sti_gaps",
  "commercial.commercial_admin_gaps",
]);

export default function AnalysisFieldCard({
  fieldId,
  result,
  language: languageProp,
  muted = false,
  onEvidenceClick,
  missing = false,
  labelOverride,
}) {
  const language = languageProp || "en";
  const label = labelOverride || fieldLabel(fieldId, language);

  if (missing) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-3.5 py-3 dark:border-slate-700 dark:bg-slate-900/40">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>
        <p className="mt-1 text-sm text-slate-500">
          {language === "es"
            ? "Campo no disponible en el análisis."
            : language === "pt"
              ? "Campo não disponível na análise."
              : "Field not available in analysis output."}
        </p>
      </div>
    );
  }

  if (!result || result.status === "not_found") return null;

  const { status, evidence_row_ids: evidenceIds = [], evidence_quality: evidenceQuality, confidence, notes, warnings = [] } =
    result;
  const isGapField = GAP_FIELD_IDS.has(fieldId);
  const formatted = formatFieldValue(result.value, result, language, fieldId);
  const isList = Array.isArray(formatted);
  const gapItems =
    isGapField && Array.isArray(result.value)
      ? result.value.map((entry) => {
          const text =
            typeof entry === "string"
              ? entry
              : entry?.text || entry?.title || entry?.message || String(entry ?? "");
          return formatGapListItem(text, language);
        })
      : null;

  const quality = evidenceQuality
    ? language === "es"
      ? `Evidencia: ${evidenceQuality}`
      : language === "pt"
        ? `Evidência: ${evidenceQuality}`
        : `Evidence: ${evidenceQuality}`
    : null;
  const confidenceLabel = confidence
    ? language === "es"
      ? `Confianza: ${confidence}`
      : language === "pt"
        ? `Confiança: ${confidence}`
        : `Confidence: ${confidence}`
    : null;

  const notesLooksDuplicate =
    notes &&
    typeof formatted === "string" &&
    (notes === formatted || formatted.includes(notes) || notes.includes(formatted));

  return (
    <div
      className={`h-full rounded-lg border px-3.5 py-3 ${
        muted
          ? "border-slate-200/80 bg-slate-50/40 opacity-75 dark:border-slate-700/80 dark:bg-slate-900/30"
          : status === "not_found"
            ? "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60"
            : status === "ambiguous"
              ? "border-amber-200 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20"
              : status === "inferred"
                ? "border-sky-200 bg-sky-50/40 dark:border-sky-900/50 dark:bg-sky-950/20"
                : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
        {showStatusChip(status) && (
          <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusChipClass(status)}`}>
            {statusLabel(status, language)}
          </span>
        )}
      </div>

      <div className="mt-1.5 text-sm font-medium leading-relaxed text-slate-900 dark:text-slate-100">
        {gapItems ? (
          <ul className="space-y-2 font-normal">
            {gapItems.map((item, index) => (
              <li key={`${item.title}-${index}`} className="text-slate-700 dark:text-slate-200">
                <span className="font-medium text-slate-900 dark:text-slate-100">{item.title}</span>
                {item.detail ? (
                  <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{item.detail}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : isList ? (
          <ul className="list-disc space-y-1 pl-4 font-normal text-slate-700 dark:text-slate-200">
            {formatted.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className={status === "not_found" ? "font-normal text-slate-500 dark:text-slate-400" : ""}>{formatted}</p>
        )}
      </div>

      {(evidenceQuality || confidence) && status === "found" && (
        <p className="mt-1.5 text-xs text-slate-400">
          {quality}
          {quality && confidenceLabel ? " · " : null}
          {confidenceLabel}
        </p>
      )}

      {notes && !notesLooksDuplicate ? (
        <p className="mt-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{notes}</p>
      ) : null}

      {result.covered_by_global_management && result.coverage_source !== "native" && (
        <p className="mt-2 text-xs font-medium text-sky-700 dark:text-sky-300">
          {language === "es"
            ? "Cobertura de gestión: cubierto por Cyber Controller"
            : language === "pt"
              ? "Cobertura de gestão: coberto pelo Cyber Controller"
              : "Management coverage: Covered by Cyber Controller"}
        </p>
      )}

      {evidenceIds.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-slate-400">
            {language === "es" ? "Evidencia:" : language === "pt" ? "Evidência:" : "Evidence:"}
          </span>
          {evidenceIds.map((id) => (
            <button
              key={id}
              type="button"
              className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              onClick={() => onEvidenceClick?.(id)}
            >
              {id}
            </button>
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-amber-700 dark:text-amber-300">
          {warnings.map((w) => (
            <li key={w}>{humanizeWarningToken(w.startsWith("⚠") ? w : `⚠ ${w}`)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
